import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import type { AccountsController } from "../../../../api/src/accounts/accounts.controller";
import type { CashSettingsController } from "../../../../api/src/cash-settings/cash-settings.controller";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";
import { NotificationContext } from "../../Notifications";
import AccountCashInfo, {
  AccountCashSettingFormState,
  cashSplitModelRefusal,
  schema as cashSettingsSchema,
  useEffectiveModel,
} from "../../cash/AccountCashInfo";
import SubmitButton from "../../components/SubmitButton";
import {
  extractServerMessage,
  useAuthenticatedMutationAsync,
} from "../../lib/api";
import { deserializeAccountPercents } from "../percents";
import { displayAccountName } from "../../lib/display";
import AccountBillingInfo, {
  AccountBillingSettings,
  schema as billingSchema,
} from "../account/AccountBillingInfo";
import { useQueryAccount } from "../account/lib";

type AccountCashBillingFormState = AccountCashSettingFormState &
  AccountBillingSettings;

const schema = cashSettingsSchema.concat(billingSchema);

// This save handler and `AccountCashBillingSettings`' are the same handler over
// the same two PUTs, under two routes. Both are exercised by one file,
// `clients/account/AccountCashBillingSettings.test.tsx`, so the pair cannot
// drift apart unnoticed.
const HouseholdAccountCashSettings = () => {
  const form = useForm<AccountCashBillingFormState>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const { accountId } = useParams();

  const { data: account } = useQueryAccount(parseInt(accountId ?? ""), {
    includeBalances: true,
  });

  const updateCashSettings = useAuthenticatedMutationAsync<
    UnpackResponse<CashSettingsController["updateAccountSetting"]>
  >(
    `/v1/cash-settings/accounts/${accountId}`,
    async (cashSettings: AccountCashSettingFormState) => ({
      method: "PUT",
      body: JSON.stringify(cashSettings),
    }),
  );

  const updateAccountBilling = useAuthenticatedMutationAsync<
    SerializedObject<UnpackResponse<AccountsController["updateAccount"]>>
  >(
    `/v1/accounts/${accountId}/billing`,
    async (data: AccountBillingSettings) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );

  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  // R6 (doc-cash-split-01a05d5f47df200b), against the account's effective
  // model (an-decision-01a0ee65bb4aad1a). AccountCashInfo renders the refusal
  // beside the Cash Split field for as long as the collision holds, so a save
  // that stops here has already said why.
  const effectiveModel = useEffectiveModel(
    typeof accountId === "undefined" ? undefined : parseInt(accountId),
  );

  const onSubmit = useMutation({
    mutationFn: async (data: AccountCashBillingFormState) => {
      try {
        // Sequential, and the cash PUT first. Under `Promise.all` both
        // requests were in flight before either answered, so a cash settings
        // rejection — which `validateCashSplitPair`
        // (`api/src/cash-settings/cash-settings.service.ts`) now issues for an
        // invalid cash split pair, R11/R15/R17 — arrived with the billing PUT
        // already committed, and the catch arm below then told the advisor
        // nothing had been saved while half of it had (APP-1565). The
        // validating request goes first so a refusal commits nothing and that
        // message is true. A transport failure on the billing PUT can still
        // leave the cash PUT committed; that inverse is APP-1565's remaining
        // half and is not reachable by validation.
        //
        // The form's own schema refuses every one of those rules it can decide
        // in the browser (`client/src/cash/AccountCashInfo.tsx`), so a pair
        // with a half missing, or naming `CASH-1`, never reaches either
        // request. It cannot decide them all — whether a symbol is a row in
        // `security_base` is knowable only at the api — which is why the
        // ordering above is what closes the remainder rather than validation.
        await updateCashSettings(data);
        const responseBilling = await updateAccountBilling(data);
        queryClient.setQueryData(
          ["accounts", parseInt(accountId ?? ""), true, false, 1],
          {
            data: {
              ...account,
              // `PUT /accounts/:id/billing` sends the account with its
              // percents on 0 to 100.
              ...deserializeAccountPercents(responseBilling.data),
            },
          },
        );
        // reset(bodyCashSettings.data);
        notificationContext.pushNotification({
          id: `cash-billing-${accountId}`,
          header: "Cash Settings Updated",
          body: `Account ${displayAccountName(
            account?.displayName,
            account?.displayNumber,
          )} cash & billing settings updated`,
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save account cash & billing settings", err);

        // The api's own sentence, when it sent one. `validateCashSplitPair`
        // refuses a cash split for one of several reasons — the symbol is
        // `CASH-1`, the symbol is in no `security_base` row, one half of the
        // pair is missing, the percentage is out of range — and each names the
        // form's own field labels. A fixed body would tell the advisor a save
        // failed without telling them which value to change, or that a value is
        // what failed at all.
        const serverMessage = extractServerMessage(err);

        notificationContext.pushNotification({
          id: `cash-billing-${accountId}`,
          header: "Failed to Save Cash & Billing Settings",
          body:
            serverMessage ??
            `Account ${displayAccountName(
              account?.displayName,
              account?.displayNumber,
            )} cash & billing settings were not saved`,
          variant: "danger",
        });
      }
    },
  });

  return (
    <>
      <Row className="mb-2">
        <Col>
          <Link to="..">{"<"} Back to List</Link>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col>
          <h3>
            {displayAccountName(account?.displayName, account?.displayNumber)}
          </h3>
        </Col>
      </Row>
      <Row>
        <Col>
          <FormProvider {...form}>
            <Form
              onSubmit={handleSubmit((data) =>
                typeof cashSplitModelRefusal(
                  effectiveModel,
                  data.cashSplitSymbol,
                ) === "undefined"
                  ? onSubmit.mutateAsync(data)
                  : Promise.resolve(),
              )}
            >
              <Row>
                <Col xxl={6}>
                  <h4>Cash Settings</h4>
                  <AccountCashInfo />
                </Col>
                <Col xxl={6}>
                  <h4>Billing Settings</h4>
                  <AccountBillingInfo />
                </Col>
              </Row>
              <Row>
                <Col>
                  <SubmitButton isSubmitting={isSubmitting} />
                </Col>
              </Row>
            </Form>
          </FormProvider>
        </Col>
      </Row>
    </>
  );
};

export default HouseholdAccountCashSettings;
